import React, { useState, useEffect } from 'react';
import { X, Phone, MessageCircle, Clock, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const FloatingContactScript = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);

  const messages = [
    {
      text: "👋 Besoin d'aide maintenant ?",
      subtext: "Intervention possible sous 2h",
      urgency: "normal",
      action: "Appelez : 06 76 58 30 38"
    },
    {
      text: "🚨 Urgence frelon asiatique ?",
      subtext: "Intervention immédiate disponible",
      urgency: "high",
      action: "Contactez-nous maintenant !"
    },
    {
      text: "⚡ Devis gratuit express",
      subtext: "Réponse en moins d'1 heure",
      urgency: "normal",
      action: "Demander un devis"
    },
    {
      text: "🔧 Intervention 7j/7 - 24h/24",
      subtext: "Même le weekend et jours fériés",
      urgency: "normal",
      action: "Vérifier disponibilité"
    }
  ];

  // Show after user has been on page for 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasInteracted) {
        setIsVisible(true);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [hasInteracted]);

  // Rotate messages every 8 seconds
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setCurrentMessage((prev) => (prev + 1) % messages.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [isVisible, messages.length]);

  // Hide on scroll interaction
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setHasInteracted(true);
      }
    };

    const handleClick = () => {
      setHasInteracted(true);
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const handleCall = () => {
    setHasInteracted(true);
    setIsVisible(false);
    window.location.href = 'tel:0676583038';
  };

  const handleWhatsApp = () => {
    setHasInteracted(true);
    setIsVisible(false);
    window.open('https://wa.me/33676583038?text=Bonjour, j\'ai besoin d\'une intervention pour un problème de nuisibles.', '_blank');
  };

  const handleClose = () => {
    setIsVisible(false);
    setHasInteracted(true);
  };

  const scrollToContact = () => {
    setHasInteracted(true);
    setIsVisible(false);
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!isVisible || hasInteracted) return null;

  const current = messages[currentMessage];

  return (
    <div className="fixed bottom-6 left-6 z-50 max-w-sm animate-in slide-in-from-bottom duration-500">
      <Card className={`shadow-2xl border-2 transform hover:scale-105 transition-all duration-300 ${
        current.urgency === 'high' 
          ? 'border-red-400 bg-gradient-to-br from-red-50 to-red-100' 
          : 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100'
      }`}>
        <CardContent className="p-4 relative">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Pulsing Indicator */}
          <div className={`absolute -top-2 -left-2 w-4 h-4 rounded-full animate-ping ${
            current.urgency === 'high' ? 'bg-red-400' : 'bg-yellow-400'
          }`}></div>
          
          <div className={`absolute -top-2 -left-2 w-4 h-4 rounded-full ${
            current.urgency === 'high' ? 'bg-red-500' : 'bg-yellow-500'
          }`}></div>

          <div className="space-y-3">
            {/* Message */}
            <div>
              <div className={`font-bold text-lg ${
                current.urgency === 'high' ? 'text-red-800' : 'text-gray-900'
              }`}>
                {current.text}
              </div>
              <div className={`text-sm ${
                current.urgency === 'high' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {current.subtext}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-2">
              <Button
                onClick={handleCall}
                className={`font-semibold flex items-center justify-center space-x-2 ${
                  current.urgency === 'high'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                }`}
              >
                <Phone className="h-4 w-4" />
                <span>06 76 58 30 38</span>
              </Button>
              
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={handleWhatsApp}
                  variant="outline"
                  className="text-green-600 border-green-300 hover:bg-green-50 text-sm py-2"
                >
                  <MessageCircle className="h-3 w-3 mr-1" />
                  WhatsApp
                </Button>
                <Button
                  onClick={scrollToContact}
                  variant="outline"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50 text-sm py-2"
                >
                  <Zap className="h-3 w-3 mr-1" />
                  Devis
                </Button>
              </div>
            </div>

            {/* Urgency Indicator */}
            {current.urgency === 'high' && (
              <div className="flex items-center space-x-2 text-red-600 text-xs font-medium">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span>INTERVENTION D'URGENCE DISPONIBLE</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FloatingContactScript;